import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SurveyRow, AnalysisResults } from './types';
import { QualityReport, QualityConfig } from './utils/dataQuality';

interface SurveyState {
  currentPage: string;
  fileData: SurveyRow[] | null;
  workshopColumn: string | null;
  axes: Record<string, string[]>;
  scaleMapping: Record<string, number>;
  analysisResults: AnalysisResults | null;
  qualityReport: QualityReport | null;
  qualityConfig: QualityConfig | null;
  setCurrentPage: (page: string) => void;
  setFileData: (data: SurveyRow[]) => void;
  setWorkshopColumn: (column: string) => void;
  setAxes: (axes: Record<string, string[]>) => void;
  addAxis: (name: string) => void;
  removeAxis: (name: string) => void;
  addQuestionsToAxis: (axisName: string, questions: string[]) => void;
  removeQuestionFromAxis: (axisName: string, question: string) => void;
  setScaleMapping: (mapping: Record<string, number>) => void;
  setAnalysisResults: (results: AnalysisResults) => void;
  setQualityReport: (report: QualityReport) => void;
  setQualityConfig: (config: QualityConfig) => void;
  clearAllData: () => void;
  hasFile: () => boolean;
  hasAxes: () => boolean;
  hasScaleMapping: () => boolean;
  hasAnalysisResults: () => boolean;
  hasQualityReport: () => boolean;
  canProceedToAnalysis: () => boolean;
}

const initialState = {
  currentPage: 'file_upload',
  fileData: null,
  workshopColumn: null,
  axes: {},
  scaleMapping: {},
  analysisResults: null,
  qualityReport: null,
  qualityConfig: null,
};

export const useStore = create<SurveyState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentPage: (page) => set({ currentPage: page }),
      setFileData: (data) => set({ fileData: data }),
      setWorkshopColumn: (column) => set({ workshopColumn: column }),
      setAxes: (axes) => set({ axes }),

      addAxis: (name) => {
        if (!name.trim()) return;
        const currentAxes = get().axes;
        if (currentAxes[name]) return;
        set({ axes: { ...currentAxes, [name]: [] } });
      },

      removeAxis: (name) => {
        const { [name]: removed, ...remainingAxes } = get().axes;
        set({ axes: remainingAxes });
      },

      addQuestionsToAxis: (axisName, questions) => {
        const currentAxes = get().axes;
        if (!currentAxes[axisName]) return;

        const updatedQuestions = [...new Set([...currentAxes[axisName], ...questions])];
        set({
          axes: {
            ...currentAxes,
            [axisName]: updatedQuestions
          }
        });
      },

      removeQuestionFromAxis: (axisName, question) => {
        const currentAxes = get().axes;
        if (!currentAxes[axisName]) return;

        set({
          axes: {
            ...currentAxes,
            [axisName]: currentAxes[axisName].filter(q => q !== question)
          }
        });
      },

      setScaleMapping: (mapping) => set({ scaleMapping: mapping }),
      setAnalysisResults: (results) => set({ analysisResults: results }),
      setQualityReport: (report) => set({ qualityReport: report }),
      setQualityConfig: (config) => set({ qualityConfig: config }),
      
      clearAllData: () => set(initialState),

      hasFile: () => {
        const state = get();
        return Boolean(state.fileData?.length > 0 && state.workshopColumn);
      },

      hasAxes: () => {
        const axes = get().axes;
        return Boolean(
          Object.keys(axes).length > 0 && 
          Object.values(axes).some(questions => questions.length > 0)
        );
      },

      hasScaleMapping: () => {
        const mapping = get().scaleMapping;
        return Boolean(Object.keys(mapping).length > 0);
      },

      hasAnalysisResults: () => Boolean(get().analysisResults),
      
      canProceedToAnalysis: () => {
        const state = get();
        const hasValidFile = Boolean(state.fileData?.length > 0 && state.workshopColumn);
        const hasValidAxes = Boolean(Object.keys(state.axes).length > 0 && Object.values(state.axes).some(questions => questions.length > 0));
        const hasValidScale = Boolean(Object.keys(state.scaleMapping).length > 0);
        
        // Debug log to help diagnose issues
        console.log('Analysis readiness check:', {
          hasValidFile,
          hasValidAxes, 
          hasValidScale,
          fileDataLength: state.fileData?.length || 0,
          workshopColumn: state.workshopColumn,
          axesCount: Object.keys(state.axes).length,
          axesWithQuestions: Object.values(state.axes).filter(q => q.length > 0).length,
          scaleMappingCount: Object.keys(state.scaleMapping).length
        });
        
        return hasValidFile && hasValidAxes && hasValidScale;
      }
    }),
    {
      name: 'survey-analysis-storage',
      partialize: (state) => ({
        axes: state.axes,
        scaleMapping: state.scaleMapping,
        analysisResults: state.analysisResults
      })
    }
  )
);